import React, { FC, forwardRef, RefObject } from 'react';
import styled, { css } from 'styled-components';
import { getResponsiveType } from '../../utils';
import { CaretDown } from '../icon/icons/CaretDown';

import { BaseFormProps, StyledBase } from './Base';
import { FormField, FormFieldProps } from './FormField';

export interface SelectOptions {
  name: string;
  value: string;
}
export interface AltSelectProps extends BaseFormProps, FormFieldProps {
  options: SelectOptions[];
  name?: string;
}

const StyledFormField = styled(FormField)`
  ${({ theme: { global, borders, colors, space, layers } }) => css`
    background-color: ${colors.grey2.default};
    border-radius: ${global.borderRadius.default};
    overflow: hidden;
    position: relative;
    width: 100%;

    label {
      position: absolute;
      top: ${space.sm};
      left: ${space.md};
      pointer-events: none;
      right: 30px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      ${getResponsiveType('titleExtraSmall')}
      z-index: ${layers.searchLabel};
    }

    select {
      background: none;
    }

    ${StyledBase} {
      appearance: none;
      background-color: ${colors.grey2.default};
      border: none;
      padding: ${space.sm} ${space.md};
      padding-top: ${space.lg};
      padding-right: 30px;
      max-width: none;
      ${getResponsiveType('bodySmall')}
      border: solid 1px ${colors.grey2.default};

      &:hover,
      &:focus {
        cursor: pointer;
        border: solid 1px ${colors.black.default};
      }
    }
  `}
`;

const StyledCaretDown = styled(CaretDown)`
  ${({ theme: { space, layers } }) => css`
    position: absolute;
    top: 14px;
    right: ${space.sm};
    pointer-events: none;
    z-index: ${layers.searchCaret};
  `}
`;

export const AltSelect: FC<AltSelectProps> = forwardRef(
  ({ label, options, id, tooltipText, tooltipInfoIcon, valid, invalid, constrained, required, name, ...rest }, ref) => (
    <StyledFormField
      label={label}
      htmlFor={id}
      tooltipText={tooltipText}
      tooltipInfoIcon={tooltipInfoIcon}
      required={required}
    >
      <StyledCaretDown />
      <StyledBase
        as="select"
        id={id}
        name={name || id}
        valid={valid}
        invalid={invalid}
        ref={ref as RefObject<HTMLSelectElement>}
        constrained={constrained}
        {...rest}
      >
        {options?.map((option, i) => (
          <option key={i} value={option.value}>
            {option.name}
          </option>
        ))}
      </StyledBase>
    </StyledFormField>
  )
);
